import { useEffect, useRef, useState } from "react";
import type { ConversationSummary } from "../types";

/** "Previous chats" dropdown.
 *
 *  `available` is a TRI-STATE: `true` renders the menu, `false` means this
 *  deployment has no conversation store (an empty dropdown would read as "you have
 *  never used this", a different and misleading claim), and `null` means we don't
 *  know yet — render nothing rather than guessing, because guessing wrong shows the
 *  user one control turning into another on launch.
 *
 *  The delete action says "Remove" rather than "Delete" on purpose: it is a soft
 *  delete that keeps the usage-analytics row, and Postgres backups keep the
 *  transcript recoverable for days afterwards. Promising deletion would be a
 *  promise we don't keep. See docs/CONVERSATION-STORE.md.
 */
export function ConversationMenu({
  available,
  conversations,
  activeId,
  loading,
  onOpen,
  onSelect,
  onRemove,
  onNewChat,
}: {
  available: boolean | null;
  conversations: ConversationSummary[];
  activeId: string | null;
  loading: boolean;
  onOpen: () => void;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onNewChat: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click and on Escape. The pane is small and a stuck-open
  // dropdown covers the whole conversation.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Strict check: `null` (unknown) and `false` (no store) both render nothing here,
  // but App.tsx only offers its fallback "New chat" button on an explicit `false`.
  if (available !== true) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) onOpen(); // refresh on open, not on every render
  };

  return (
    <div className="tp-history" ref={wrapRef}>
      <button
        className="tp-history-btn"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="listbox"
        title="Previous chats"
      >
        <span aria-hidden="true">🕘</span> Previous chats
      </button>

      {open && (
        <div className="tp-history-menu" role="listbox">
          <button
            className="tp-history-new"
            onClick={() => {
              setOpen(false);
              onNewChat();
            }}
          >
            + New chat
          </button>

          {loading && <div className="tp-history-empty">Loading…</div>}

          {!loading && !conversations.length && <div className="tp-history-empty">No previous chats yet.</div>}

          {!loading &&
            conversations.map((c) => (
              <div key={c.id} className={`tp-history-row${c.id === activeId ? " is-active" : ""}`} role="option" aria-selected={c.id === activeId}>
                <button
                  className="tp-history-open"
                  onClick={() => {
                    setOpen(false);
                    onSelect(c.id);
                  }}
                  title={c.contentPurged ? "This chat's messages have aged out" : undefined}
                >
                  <span className="tp-history-title">{c.title || "Untitled chat"}</span>
                  <span className="tp-history-meta">
                    {formatWhen(c.lastTurnAt)}
                    {c.contentPurged ? " · aged out" : ""}
                  </span>
                </button>
                <button
                  className="tp-history-remove"
                  aria-label={`Remove ${c.title || "chat"} from previous chats`}
                  title="Remove from this list"
                  onClick={() => onRemove(c.id)}
                >
                  ×
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/** Relative for recent things, absolute once it stops being useful.
 *  Formatted client-side deliberately: App Service runs in UTC, so a server-side
 *  format would show the wrong local time — the same reason mailbox row dates are
 *  sent as raw ISO. */
function formatWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
